import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Button, Tag, Spin, Typography, Space, Modal, Form, Select, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { EditOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { ReviewTask } from '../../types';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ReviewTaskDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState<ReviewTask | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取測評任務詳情
        const taskData = await api.reviewTasks.getById(id as string);
        setTask(taskData as ReviewTask);
      } catch (error) {
        console.error('Error fetching review task details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // 更新任務狀態
  const handleUpdateStatus = async (values: Partial<ReviewTask>) => {
    setSubmitting(true);
    try {
      await api.reviewTasks.updateStatus(id as string, values);
      message.success(t('common.success'));

      // 更新任務數據
      const taskData = await api.reviewTasks.getById(id as string);
      setTask(taskData as ReviewTask);

      setStatusModalVisible(false);
    } catch (error) {
      console.error('Error updating task status:', error);
      message.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // 打開狀態更新模態框
  const showStatusModal = () => {
    if (task) {
      statusForm.setFieldsValue({
        review_status: task.review_status,
        delivery_status: task.delivery_status,
        exception_handling: task.exception_handling,
        remarks: task.remarks,
      });
      setStatusModalVisible(true);
    }
  };

  // 解析關鍵詞 JSON 字符串
  const parseKeywords = (keywordsStr: string): string[] => {
    try {
      return JSON.parse(keywordsStr);
    } catch (error) {
      return [];
    }
  };

  if (loading) {
    return <Spin size="large" />;
  }

  if (!task) {
    return <div>{t('common.noData')}</div>;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/review-tasks')}
        >
          {t('common.back')}
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/review-tasks/${id}/edit`)}
        >
          {t('common.edit')}
        </Button>
      </Space>

      <Title level={2}>{t('reviewTasks.title')} - {task.task_id}</Title>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions title={t('reviewTasks.title')} bordered>
          <Descriptions.Item label={t('reviewTasks.taskId')}>{task.task_id}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.storeId')}>{task.store_id}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.platform')}>{task.platform}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.keywords')}>
            {parseKeywords(task.keywords).map((keyword: string, index: number) => (
              <Tag key={index} color="blue">{keyword}</Tag>
            ))}
          </Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.reviewAccount')}>{task.review_account}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.accountAlias')}>{task.account_alias}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.phone')}>{task.phone}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.deliveryStore')}>{task.delivery_store}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.amount')}>{task.amount}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.orderId')}>{task.order_id}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.date')}>{task.date}</Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.reviewStatus')}>
            <Tag color={task.review_status === '已評論' ? 'success' : 'warning'}>
              {task.review_status}
            </Tag>
            <Button
              type="link"
              size="small"
              onClick={showStatusModal}
              style={{ marginLeft: 8 }}
            >
              {t('reviewTasks.updateStatus')}
            </Button>
          </Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.deliveryStatus')}>
            <Tag color={task.delivery_status === '已收貨' ? 'success' : 'warning'}>
              {task.delivery_status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.isVirtual')}>
            {task.is_virtual ? t('common.yes') : t('common.no')}
          </Descriptions.Item>
          <Descriptions.Item label={t('reviewTasks.createdAt')}>{task.created_at}</Descriptions.Item>
        </Descriptions>

        {task.exception_handling && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>{t('reviewTasks.exceptionHandling')}</Title>
            <Paragraph>{task.exception_handling}</Paragraph>
          </div>
        )}

        {task.remarks && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>{t('reviewTasks.remarks')}</Title>
            <Paragraph>{task.remarks}</Paragraph>
          </div>
        )}
      </Card>

      {/* 狀態更新模態框 */}
      <Modal
        title={t('reviewTasks.updateStatus')}
        visible={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleUpdateStatus}
        >
          <Form.Item
            name="review_status"
            label={t('reviewTasks.reviewStatus')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="已評論">{t('reviewTasks.reviewStatuses.reviewed')}</Option>
              <Option value="未評論">{t('reviewTasks.reviewStatuses.unreviewed')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_status"
            label={t('reviewTasks.deliveryStatus')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="已收貨">{t('reviewTasks.deliveryStatuses.received')}</Option>
              <Option value="未收貨">{t('reviewTasks.deliveryStatuses.unreceived')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="exception_handling"
            label={t('reviewTasks.exceptionHandling')}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="remarks"
            label={t('reviewTasks.remarks')}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReviewTaskDetail;
